import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGameStore from '../../stores/gameStore';

/** Timer state shape stored as JSON in a string variable (matches engine TimerState). */
interface TimerStateJson {
  mode: 'blind' | 'stopwatch' | 'max' | 'min';
  startedAt: number | null;
  stoppedAt: number | null;
  minSeconds: number | null;
  maxSeconds: number | null;
}

const DEFAULT_STATE: TimerStateJson = {
  mode: 'stopwatch',
  startedAt: null,
  stoppedAt: null,
  minSeconds: null,
  maxSeconds: null
};

function parseTimerState(value: string | undefined | null): TimerStateJson {
  if (value === undefined || value === null || value === '') {
    return { ...DEFAULT_STATE };
  }
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_STATE };
    const mode = ['blind', 'stopwatch', 'max', 'min'].includes(parsed.mode as string)
      ? (parsed.mode as TimerStateJson['mode'])
      : 'stopwatch';
    const startedAt =
      typeof parsed.startedAt === 'number' && Number.isFinite(parsed.startedAt) ? parsed.startedAt : null;
    const stoppedAt =
      typeof parsed.stoppedAt === 'number' && Number.isFinite(parsed.stoppedAt) ? parsed.stoppedAt : null;
    const minSeconds =
      typeof parsed.minSeconds === 'number' && Number.isFinite(parsed.minSeconds) && parsed.minSeconds >= 0
        ? parsed.minSeconds
        : null;
    const maxSeconds =
      typeof parsed.maxSeconds === 'number' && Number.isFinite(parsed.maxSeconds) && parsed.maxSeconds >= 0
        ? parsed.maxSeconds
        : null;
    return { mode, startedAt, stoppedAt, minSeconds, maxSeconds };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function getElapsedSeconds(state: TimerStateJson, nowMs: number): number {
  if (state.startedAt === null) return 0;
  const endMs = state.stoppedAt !== null ? state.stoppedAt : nowMs;
  return Math.floor(Math.max(0, endMs - state.startedAt) / 1000);
}

function getRemainingSeconds(state: TimerStateJson, nowMs: number): number | null {
  if (state.mode !== 'max' || state.maxSeconds === null) return null;
  const elapsed = getElapsedSeconds(state, nowMs);
  return Math.max(0, state.maxSeconds - elapsed);
}

function formatMMSS(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(m)}:${pad(s)}`;
}

export type TimerDisplayType = 'blind' | 'stopwatch' | 'countdown' | 'min';

export interface TimerDisplayProps {
  timerVar: string;
  displayType?: TimerDisplayType | undefined;
  className?: string | undefined;
  /** Start time (UTC ms) from engine; when set, component ticks locally without reading the variable again. */
  startedAt?: string | undefined;
  /** Stop time (UTC ms) when timer was stopped. */
  stoppedAt?: string | undefined;
  /** Max seconds for countdown mode (from engine). */
  maxSeconds?: string | undefined;
}

function parseOptionalMs(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalSeconds(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Renders a timer. When startedAt (and optionally stoppedAt/maxSeconds) are passed from the engine
 * markup, the component updates itself every second without reading the variable. Otherwise falls back
 * to the timer variable in the game store.
 * - blind: clock with spinning hands (no numbers).
 * - stopwatch / min: elapsed time (MM:SS), updates every second when running.
 * - countdown: remaining time (MM:SS) or "Time's up" when zero.
 */
const TimerDisplay: React.FC<TimerDisplayProps> = ({
  timerVar,
  displayType = 'stopwatch',
  className = '',
  startedAt: startedAtProp,
  stoppedAt: stoppedAtProp,
  maxSeconds: maxSecondsProp
}) => {
  const rawValue = useGameStore(
    useCallback((state) => state.userVariableValues[timerVar]?.value ?? '', [timerVar])
  );

  const stateFromStore = useMemo(() => parseTimerState(rawValue), [rawValue]);
  const startedAtMs = parseOptionalMs(startedAtProp);
  const stoppedAtMs = parseOptionalMs(stoppedAtProp);
  const maxSecondsFromProp = parseOptionalSeconds(maxSecondsProp);

  const useProps = startedAtMs !== null;
  const state = useMemo(() => {
    if (useProps) {
      const maxSec = maxSecondsFromProp ?? stateFromStore.maxSeconds;
      return {
        ...DEFAULT_STATE,
        mode: maxSec !== null ? 'max' : DEFAULT_STATE.mode,
        startedAt: startedAtMs,
        stoppedAt: stoppedAtMs,
        maxSeconds: maxSec
      } as TimerStateJson;
    }
    return stateFromStore;
  }, [useProps, startedAtMs, stoppedAtMs, maxSecondsFromProp, stateFromStore]);

  const isRunning = state.startedAt !== null && state.stoppedAt === null;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const elapsed = useMemo(() => getElapsedSeconds(state, now), [state, now]);
  const remaining = useMemo(() => getRemainingSeconds(state, now), [state, now]);

  if (displayType === 'blind') {
    return (
      <div
        className={`timer-display timer-display-blind ${className}`}
        data-testid="timer-display-blind"
        role="timer"
        aria-label="Timing in progress"
      >
        <div className="timer-display-blind-clock" aria-hidden>
          <div className="timer-display-blind-clock-minute-hand" />
          <div className="timer-display-blind-clock-hand" />
          <div className="timer-display-blind-clock-center" />
        </div>
      </div>
    );
  }

  if (displayType === 'countdown') {
    const rem = remaining;
    const isZero = rem !== null && rem <= 0;
    return (
      <div
        className={`timer-display timer-display-countdown timer-display-led ${className}`}
        data-testid="timer-display-countdown"
        role="timer"
        aria-live="polite"
        aria-label={isZero ? "Time's up" : `Time remaining: ${rem !== null ? formatMMSS(rem) : '0:00'}`}
      >
        {isZero ? (
          <span className="timer-display-times-up">Time's up</span>
        ) : (
          <span className="timer-display-value">{rem !== null ? formatMMSS(rem) : '0:00'}</span>
        )}
      </div>
    );
  }

  // stopwatch and min: show elapsed (digital LED style, centered, large)
  return (
    <div
      className={`timer-display timer-display-elapsed timer-display-led ${className}`}
      data-testid="timer-display-elapsed"
      role="timer"
      aria-live="polite"
      aria-label={`Elapsed time: ${formatMMSS(elapsed)}`}
    >
      <span className="timer-display-value">{formatMMSS(elapsed)}</span>
    </div>
  );
};

export default TimerDisplay;
